import { useEffect, useRef, useState } from "react";
import {
  createFileRoute,
  Navigate,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { Text } from "@/shared/ui/typography";
import { AuthHeading } from "@/shared/ui/auth-heading";
import { useTranslation, T } from "@/shared/i18n";
import { SubscriptionLayout } from "@/shared/ui/subscription-layout";
import { WizardProgress } from "@/pages/subscription-setup/ui/wizard-progress";
import {
  OnboardingProgress,
  getStepStatus,
  type ProgressStep,
} from "@/features/onboarding-conversation/ui/onboarding-progress";
import { usePollSubscriptionStatus } from "@/pages/subscription-setup/api/poll-subscription-status";
import { clearAuthCache } from "@/shared/lib/auth";
import { router } from "@/shared/lib/router";
import { toTrackingCode, track } from "@/shared/lib/analytics";

type CallbackSearch = {
  redirect_status?: string;
};

const TIMEOUT_MS = 30_000;
const MIN_DISPLAY_MS = 10_000;
const STEP_INTERVAL_MS = 2_500;

const STEP_KEYS = [
  "Processing your payment",
  "Setting up your subscription",
  "Preparing your dashboard",
  "Almost there",
];

const SubscriptionProcessing = () => {
  const { companySlug } = useParams({
    from: "/_authed/_noSidebar/$companySlug/subscribe/callback",
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isActive, plan, term } = usePollSubscriptionStatus();
  const [phase, setPhase] = useState(0);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const hasNavigated = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setPhase((p) => Math.min(p + 1, STEP_KEYS.length - 1));
    }, STEP_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setMinTimeElapsed(true), MIN_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isActive && minTimeElapsed && !hasNavigated.current) {
      hasNavigated.current = true;
      track("billing.checkout.succeeded", {
        plan: toTrackingCode(plan),
        term: toTrackingCode(term),
      });
      // Refresh auth context so the $companySlug guard sees the new
      // hasActiveSubscription. Without this the in-memory cachedMe (60s
      // TTL) stays stale — most visibly after iDEAL/Wero, where the
      // off-site redirect causes a fresh page load that snapshots `me`
      // *before* the webhook lands, bouncing "View tenders" back to the
      // subscription page.
      void (async () => {
        try {
          clearAuthCache();
          await router.invalidate();
        } finally {
          // Always navigate — if invalidate() throws, the guard may bounce
          // back to /subscribe (the original bug), but that's strictly
          // better than leaving the user stuck on the callback page with
          // hasNavigated already flipped, which would also block the
          // 30s timeout fallback.
          navigate({
            to: "/$companySlug/subscribe/success",
            params: { companySlug },
            replace: true,
          });
        }
      })();
    }
  }, [isActive, minTimeElapsed, navigate, companySlug, plan, term]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasNavigated.current) {
        hasNavigated.current = true;
        track("billing.checkout.failed");
        navigate({
          to: "/$companySlug/subscribe/failed",
          params: { companySlug },
          search: { reason: "timeout" },
          replace: true,
        });
      }
    }, TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [navigate, companySlug]);

  const steps: ProgressStep[] = STEP_KEYS.map((key, index) => ({
    id: `step-${index}`,
    label: t(key),
    status: getStepStatus(phase, index),
  }));

  return (
    <SubscriptionLayout progress={<WizardProgress current={4} />}>
      <div className="w-full space-y-8">
        <div className="mx-auto max-w-xl space-y-2 text-center">
          <AuthHeading className="text-3xl">
            <T id="Setting up your account" />
          </AuthHeading>
          <Text className="text-muted-foreground">
            <T id="Please wait while we finalize everything..." />
          </Text>
        </div>
        <div className="mx-auto w-fit">
          <OnboardingProgress steps={steps} />
        </div>
      </div>
    </SubscriptionLayout>
  );
};

const CallbackRoute = () => {
  const { companySlug } = useParams({
    from: "/_authed/_noSidebar/$companySlug/subscribe/callback",
  });
  const { redirect_status } = Route.useSearch();
  const isSucceeded = redirect_status === "succeeded";

  // Stripe sent the user back without a confirmed setup. The ref keeps
  // StrictMode's second effect run from counting it twice.
  const failureTracked = useRef(false);
  useEffect(() => {
    if (isSucceeded || failureTracked.current) return;
    failureTracked.current = true;
    track("billing.checkout.failed");
  }, [isSucceeded]);

  if (isSucceeded) {
    return <SubscriptionProcessing />;
  }

  return (
    <Navigate
      to="/$companySlug/subscribe/failed"
      params={{ companySlug }}
      replace
    />
  );
};

export const Route = createFileRoute(
  "/_authed/_noSidebar/$companySlug/subscribe/callback",
)({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => ({
    redirect_status: search.redirect_status as string | undefined,
  }),
  component: CallbackRoute,
});
